import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import '../styles/Schedule.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const RANDOM_REMINDER_VALUE = 'random';

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const emptyForm = {
  name: '',
  minute: '0',
  hour: '9',
  day_of_month: '*',
  month_of_year: '*',
  day_of_week: '1,3',
  content: RANDOM_REMINDER_VALUE, // 'random' or a template id (string)
  enabled: true,
};

/** Best-effort human-readable summary of a 5-field cron expression. Handles the
 * common cases (fixed time, weekday lists, "every X"); falls back to raw cron
 * syntax for anything more exotic. */
function describeCron({ minute, hour, day_of_month, month_of_year, day_of_week }) {
  const isWild = (v) => v === '*' || v === '';
  const timeOk = /^\d{1,2}$/.test(minute) && /^\d{1,2}$/.test(hour);
  const time = timeOk ? `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}` : null;

  let when = 'every minute';
  if (timeOk) {
    when = `at ${time}`;
  }

  const parts = [when];

  if (!isWild(day_of_week)) {
    const names = day_of_week.split(',').map((d) => WEEKDAY_NAMES[parseInt(d, 10)] || d).filter(Boolean);
    if (names.length) parts.push(`on ${names.join(', ')}`);
  }
  if (!isWild(day_of_month)) {
    parts.push(`on day ${day_of_month} of the month`);
  }
  if (!isWild(month_of_year)) {
    const names = month_of_year.split(',').map((m) => MONTH_NAMES[parseInt(m, 10)] || m).filter(Boolean);
    if (names.length) parts.push(`in ${names.join(', ')}`);
  }

  if (!timeOk) {
    return `cron: ${minute} ${hour} ${day_of_month} ${month_of_year} ${day_of_week}`;
  }
  return parts.join(', ');
}

function formatCountdown(ms) {
  if (ms == null) return '—';
  if (ms <= 0) return 'due now';
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function Schedule({ user }) {
  const [jobs, setJobs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const roles = user?.roles || [];
  const canAccess = roles.includes('schedule_editor') || roles.includes('admin');

  useEffect(() => {
    if (!canAccess) return;
    fetchJobs();
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  // Live countdown ticker
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Refresh next-run estimates from the server periodically (they're computed
  // server-side, so a stale client clock or DST change won't drift silently).
  useEffect(() => {
    if (!canAccess) return;
    const interval = setInterval(fetchJobs, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/scheduled-broadcasts/`);
      setJobs(res.data);
    } catch (err) {
      console.error('Error fetching scheduled broadcasts:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/templates/`);
      setTemplates(res.data);
    } catch {}
  };

  const openNewForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
    setStatusMsg('');
  };

  const startEdit = (job) => {
    setForm({
      name: job.name,
      minute: job.minute,
      hour: job.hour,
      day_of_month: job.day_of_month,
      month_of_year: job.month_of_year,
      day_of_week: job.day_of_week,
      content: job.use_random_reminder ? RANDOM_REMINDER_VALUE : String(job.template || ''),
      enabled: job.enabled,
    });
    setEditingId(job.id);
    setShowForm(true);
    setStatusMsg('');
  };

  const cancelForm = () => {
    setShowForm(false);
    setForm(emptyForm);
    setEditingId(null);
  };

  const buildPayload = () => {
    const useRandom = form.content === RANDOM_REMINDER_VALUE;
    return {
      name: form.name.trim(),
      minute: form.minute.trim() || '*',
      hour: form.hour.trim() || '*',
      day_of_month: form.day_of_month.trim() || '*',
      month_of_year: form.month_of_year.trim() || '*',
      day_of_week: form.day_of_week.trim() || '*',
      use_random_reminder: useRandom,
      template: useRandom ? null : form.content || null,
      enabled: form.enabled,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      const payload = buildPayload();
      if (editingId) {
        await axios.put(`${API_BASE_URL}/scheduled-broadcasts/${editingId}/`, payload);
        setStatusMsg('Schedule updated.');
      } else {
        await axios.post(`${API_BASE_URL}/scheduled-broadcasts/`, payload);
        setStatusMsg('Schedule created.');
      }
      setStatusType('success');
      cancelForm();
      fetchJobs();
    } catch (err) {
      const apiError = typeof err.response?.data === 'object'
        ? (err.response?.data?.error || err.response?.data?.non_field_errors?.[0])
        : null;
      setStatusMsg('Error: ' + (apiError || err.message));
      setStatusType('error');
    }
  };

  const toggleEnabled = async (job) => {
    try {
      await axios.put(`${API_BASE_URL}/scheduled-broadcasts/${job.id}/`, {
        name: job.name,
        minute: job.minute,
        hour: job.hour,
        day_of_month: job.day_of_month,
        month_of_year: job.month_of_year,
        day_of_week: job.day_of_week,
        use_random_reminder: job.use_random_reminder,
        template: job.template,
        enabled: !job.enabled,
      });
      fetchJobs();
    } catch (err) {
      setStatusMsg('Error toggling schedule.');
      setStatusType('error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/scheduled-broadcasts/${id}/`);
      setConfirmDelete(null);
      setStatusMsg('Schedule deleted.');
      setStatusType('success');
      fetchJobs();
    } catch (err) {
      setStatusMsg('Error deleting schedule.');
      setStatusType('error');
    }
  };

  const countdowns = useMemo(() => {
    const map = {};
    for (const job of jobs) {
      map[job.id] = job.next_run_at ? new Date(job.next_run_at).getTime() - now : null;
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, now]);

  if (!canAccess) return <div className="restricted">You do not have permission to access this page.</div>;

  return (
    <div className="schedule-page">
      <h2>Schedule</h2>
      <p className="schedule-intro">
        Recurring broadcasts — e.g. Monday &amp; Wednesday training reminders, or an
        ad-hoc recurring meeting invite. Each job sends to every member with the
        "Receives Reminders" role.
      </p>

      {statusMsg && <div className={`status-msg status-${statusType}`}>{statusMsg}</div>}

      {!showForm && (
        <button type="button" className="btn-new-schedule" onClick={openNewForm}>+ New Scheduled Broadcast</button>
      )}

      {showForm && (
        <div className="schedule-form-section">
          <h3>{editingId ? 'Edit Scheduled Broadcast' : 'New Scheduled Broadcast'}</h3>
          <form onSubmit={handleSubmit} className="schedule-form">
            <input
              type="text"
              placeholder="Name (e.g. Mon/Wed Training Reminder)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />

            <div className="cron-fields">
              <label>
                Minute
                <input value={form.minute} onChange={(e) => setForm({ ...form, minute: e.target.value })} placeholder="0" />
              </label>
              <label>
                Hour
                <input value={form.hour} onChange={(e) => setForm({ ...form, hour: e.target.value })} placeholder="9" />
              </label>
              <label>
                Day of month
                <input value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} placeholder="*" />
              </label>
              <label>
                Month
                <input value={form.month_of_year} onChange={(e) => setForm({ ...form, month_of_year: e.target.value })} placeholder="*" />
              </label>
              <label>
                Day of week
                <input value={form.day_of_week} onChange={(e) => setForm({ ...form, day_of_week: e.target.value })} placeholder="1,3" />
              </label>
            </div>
            <p className="cron-hint">
              Standard cron format (0=Sunday .. 6=Saturday). Comma-separate multiple values, e.g. <code>1,3</code> for Mon &amp; Wed.
              Preview: <strong>{describeCron(form)}</strong>
            </p>

            <label className="content-label">
              Content
              <select value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}>
                <option value={RANDOM_REMINDER_VALUE}>Random Reminder (picks one per recipient)</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>

            <label className="enabled-label">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              Enabled
            </label>

            <div className="form-actions">
              <button type="submit" className="btn-save">{editingId ? 'Update' : 'Create'}</button>
              <button type="button" className="btn-cancel" onClick={cancelForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="schedule-list-section">
        <h3>Scheduled Broadcasts ({jobs.length})</h3>
        {jobs.length === 0 ? (
          <p className="no-data">No scheduled broadcasts yet.</p>
        ) : (
          <ul className="schedule-list">
            {jobs.map((job) => (
              <li key={job.id} className={`schedule-item ${job.enabled ? '' : 'disabled'}`}>
                <div className="schedule-info">
                  <div className="schedule-header-row">
                    <strong className="schedule-name">{job.name}</strong>
                    <label className="toggle-switch" title={job.enabled ? 'Disable' : 'Enable'}>
                      <input type="checkbox" checked={job.enabled} onChange={() => toggleEnabled(job)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                  <p className="schedule-cron">{describeCron(job)}</p>
                  <p className="schedule-content">
                    Content: <strong>{job.use_random_reminder ? 'Random Reminder' : (job.template_name || '—')}</strong>
                  </p>
                  <p className="schedule-countdown">
                    {job.enabled ? (
                      <>Next run in <strong>{formatCountdown(countdowns[job.id])}</strong></>
                    ) : (
                      <span className="disabled-text">Disabled</span>
                    )}
                  </p>
                  {job.last_run_at && (
                    <p className="schedule-last-run">
                      Last run: {new Date(job.last_run_at).toLocaleString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
                <div className="schedule-actions">
                  <button className="btn-edit" onClick={() => startEdit(job)}>Edit</button>
                  {confirmDelete === job.id ? (
                    <>
                      <span className="confirm-text">Delete?</span>
                      <button className="btn-confirm-delete" onClick={() => handleDelete(job.id)}>Yes</button>
                      <button className="btn-cancel" onClick={() => setConfirmDelete(null)}>No</button>
                    </>
                  ) : (
                    <button className="btn-delete" onClick={() => setConfirmDelete(job.id)}>Delete</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default Schedule;
